// Educational guides — GEO-optimised (Island Test): self-contained, cited, structured
// Each guide is bilingual and includes FAQ schema-ready Q&A
import { pick } from "./translations";

const REVIEWED = {
  en: "Reviewed by the Rihai legal team",
  hi: "Rihai विधि टीम द्वारा समीक्षित",
};
const LAST_UPDATED = "2026-08-19";

export const GUIDES = {
  "bail-process": {
    slug: "bail-process",
    order: 1,
    icon: "Scale",
    title: {
      en: "The Bail Process in India — a Plain-Language Guide (2026)",
      hi: "भारत में ज़मानत की प्रक्रिया — एक सरल भाषा में गाइड (2026)",
    },
    metaTitle: {
      en: "The Bail Process in India (BNSS 2023): Full Guide by Rihai",
      hi: "भारत में ज़मानत की प्रक्रिया (BNSS 2023): Rihai की पूरी गाइड",
    },
    metaDescription: {
      en: "Complete plain-language guide to bail in India under BNSS 2023 (formerly CrPC). Regular, anticipatory, interim bail — sections, timelines, courts, documents. No jargon.",
      hi: "BNSS 2023 (पहले CrPC) के तहत भारत में ज़मानत की पूरी सरल भाषा में गाइड। नियमित, अग्रिम, अंतरिम ज़मानत — धाराएँ, समय-सीमा, अदालतें, दस्तावेज़।",
    },
    intro: {
      en: "Bail is a right — not a favour. If your loved one has been arrested in India, bail is the legal permission that lets them wait for trial at home instead of in jail. This guide walks you through every kind of bail, the exact sections of the new BNSS 2023 (which replaced CrPC on 1 July 2024), what documents matter, and how long each step actually takes.",
      hi: "ज़मानत एक अधिकार है — कोई एहसान नहीं। अगर आपके अपने की भारत में गिरफ़्तारी हुई है, तो ज़मानत वह कानूनी अनुमति है जो उन्हें जेल में नहीं बल्कि घर पर मुक़दमे का इंतज़ार करने देती है। यह गाइड आपको हर तरह की ज़मानत, नए BNSS 2023 (जिसने 1 जुलाई 2024 को CrPC को बदला) की धाराएँ, ज़रूरी दस्तावेज़ और असली समय-सीमा — सब बताएगी।",
    },
    keyPoints: {
      en: [
        "Bail is a right in most non-heinous cases — you do not need to plead for mercy.",
        "There are 3 kinds: Regular bail (Sec. 480 BNSS), Anticipatory bail (Sec. 482 BNSS), and Interim bail.",
        "For bailable offences, bail is granted as a matter of right — no discretion of the court.",
        "For non-bailable offences, the court weighs flight risk, evidence tampering, and severity.",
        "You do not need to pay bribes to court staff at any stage. Not one rupee.",
      ],
      hi: [
        "अधिकांश गैर-जघन्य मामलों में ज़मानत एक अधिकार है — दया माँगने की ज़रूरत नहीं।",
        "तीन प्रकार हैं: नियमित ज़मानत (धारा 480 BNSS), अग्रिम ज़मानत (धारा 482 BNSS), और अंतरिम ज़मानत।",
        "ज़मानती अपराधों में ज़मानत अधिकार से मिलती है — अदालत को विवेकाधिकार नहीं।",
        "ग़ैर-ज़मानती अपराधों में अदालत भागने का जोखिम, साक्ष्य के साथ छेड़छाड़ और गंभीरता देखती है।",
        "किसी भी चरण में अदालत के स्टाफ को रिश्वत देने की ज़रूरत नहीं। एक रुपया भी नहीं।",
      ],
    },
    sections: [
      {
        h: { en: "The three kinds of bail — clearly explained", hi: "ज़मानत के तीन प्रकार — साफ-साफ" },
        items: [
          {
            k: { en: "Regular Bail (Section 480 BNSS)", hi: "नियमित ज़मानत (धारा 480 BNSS)" },
            v: {
              en: "Applied for AFTER arrest. Moved before the Magistrate first, and if refused, before the Sessions Court, and finally the High Court. This is the most common type of bail application in India.",
              hi: "गिरफ़्तारी के बाद लगती है। पहले मजिस्ट्रेट के सामने, इनकार होने पर सत्र न्यायालय, फिर उच्च न्यायालय। भारत में सबसे आम प्रकार।",
            },
          },
          {
            k: { en: "Anticipatory Bail (Section 482 BNSS)", hi: "अग्रिम ज़मानत (धारा 482 BNSS)" },
            v: {
              en: "Applied for BEFORE arrest, when you fear an FIR may be filed or arrest may be imminent. Filed at Sessions Court or High Court. Once granted, the police cannot arrest without notifying the court.",
              hi: "गिरफ़्तारी से पहले, जब FIR या गिरफ़्तारी की आशंका हो। सत्र न्यायालय या उच्च न्यायालय में। मंज़ूर होने पर पुलिस अदालत को बताए बिना गिरफ़्तार नहीं कर सकती।",
            },
          },
          {
            k: { en: "Interim Bail", hi: "अंतरिम ज़मानत" },
            v: {
              en: "A short-term, emergency bail granted for a few days while the main bail application is being heard. Useful when the accused is in urgent medical need or a family emergency exists.",
              hi: "मुख्य आवेदन के दौरान कुछ दिनों की अस्थायी आपात ज़मानत। चिकित्सा या पारिवारिक आपात में उपयोगी।",
            },
          },
        ],
      },
      {
        h: { en: "What documents you need", hi: "कौन-से दस्तावेज़ चाहिए" },
        list: {
          en: [
            "Certified copy of the FIR",
            "Arrest memo (given at the time of arrest — legally mandatory)",
            "ID proof of the accused (Aadhaar, PAN or Voter ID)",
            "ID and address proof of two sureties (people who vouch for the accused)",
            "Any medical documents if you're claiming health grounds",
            "Property documents if a surety bond is on immovable property",
          ],
          hi: [
            "FIR की प्रमाणित प्रति",
            "गिरफ़्तारी मेमो (गिरफ़्तारी के समय मिलता है — कानूनी रूप से अनिवार्य)",
            "आरोपी का पहचान प्रमाण (आधार/PAN/वोटर ID)",
            "दो ज़मानतियों का पहचान और पते का प्रमाण",
            "स्वास्थ्य आधार के मामले में चिकित्सा दस्तावेज़",
            "अचल संपत्ति पर ज़मानत होने पर संपत्ति के कागज़",
          ],
        },
      },
      {
        h: { en: "The court hierarchy — where your case goes", hi: "अदालती पदानुक्रम — केस कहाँ जाएगा" },
        list: {
          en: [
            "First: Judicial Magistrate — handles remand, initial bail for most offences",
            "Second: Sessions Court — if magistrate refuses, or for serious offences from the start",
            "Third: High Court — if sessions refuses, or for anticipatory bail in grave matters",
            "Fourth: Supreme Court — only in extraordinary matters or Article 136 SLP",
          ],
          hi: [
            "पहले: न्यायिक मजिस्ट्रेट — रिमांड और अधिकांश मामलों की प्रारंभिक ज़मानत",
            "दूसरे: सत्र न्यायालय — मजिस्ट्रेट के इनकार पर, या गंभीर अपराधों में सीधे",
            "तीसरे: उच्च न्यायालय — सत्र के इनकार पर, या गंभीर मामलों में अग्रिम ज़मानत",
            "चौथे: उच्चतम न्यायालय — असाधारण मामलों में या अनुच्छेद 136 SLP",
          ],
        },
      },
    ],
    faqs: [
      {
        q: { en: "Is bail a right in India?", hi: "क्या भारत में ज़मानत एक अधिकार है?" },
        a: {
          en: "For bailable offences (defined in Schedule 1 of BNSS), bail is a matter of right. The court cannot refuse. For non-bailable offences, bail is at the court's discretion — but the presumption of innocence and Article 21 of the Constitution still weigh heavily in the accused's favour.",
          hi: "ज़मानती अपराधों (BNSS की अनुसूची 1) में ज़मानत अधिकार से मिलती है — अदालत मना नहीं कर सकती। ग़ैर-ज़मानती में अदालत के विवेक पर है, लेकिन निर्दोषिता की धारणा और अनुच्छेद 21 आरोपी के पक्ष में भारी हैं।",
        },
      },
      {
        q: { en: "How fast can bail be granted?", hi: "ज़मानत कितनी जल्दी मिल सकती है?" },
        a: {
          en: "For bailable offences, at the police station itself the moment sureties are furnished. For non-bailable at magistrate: same day to 2 days. Sessions Court: 2–7 days. High Court: 3–15 days. Anticipatory bail: 3–10 days typically.",
          hi: "ज़मानती अपराधों में — पुलिस स्टेशन पर ही, ज़मानतदार पेश करते ही। मजिस्ट्रेट में ग़ैर-ज़मानती: उसी दिन से 2 दिन। सत्र न्यायालय: 2–7 दिन। उच्च न्यायालय: 3–15 दिन। अग्रिम ज़मानत: आमतौर पर 3–10 दिन।",
        },
      },
      {
        q: { en: "Can bail be cancelled once granted?", hi: "क्या मंज़ूर की गई ज़मानत रद्द हो सकती है?" },
        a: {
          en: "Yes — under Section 483 BNSS, bail can be cancelled if the accused violates conditions (tampers with evidence, threatens witnesses, absconds, or commits another offence). The prosecution or complainant can apply.",
          hi: "हाँ — धारा 483 BNSS के तहत, यदि आरोपी शर्तों का उल्लंघन करे (साक्ष्य से छेड़छाड़, गवाहों को धमकाना, फ़रार होना, या नया अपराध) तो ज़मानत रद्द हो सकती है।",
        },
      },
      {
        q: { en: "What is a surety and what does it cost?", hi: "ज़मानतदार क्या होता है और खर्च?" },
        a: {
          en: "A surety is a person who guarantees the accused will appear in court. Usually 1–2 sureties needed. They sign a bail bond for a specific amount (e.g., ₹25,000). No money is paid unless the accused absconds. Sureties don't need cash — they just need verifiable ID and, in some cases, property proof.",
          hi: "ज़मानतदार वह व्यक्ति है जो आरोपी की अदालत में उपस्थिति की गारंटी देता है। आमतौर पर 1–2 चाहिए। वे एक तय राशि (जैसे ₹25,000) का बांड भरते हैं। पैसा तब तक नहीं देना होता जब तक आरोपी फ़रार न हो।",
        },
      },
    ],
    sources: [
      { label: "Bharatiya Nagarik Suraksha Sanhita, 2023 (BNSS)", url: "https://www.mha.gov.in/en/notifications/bharatiya-nagarik-suraksha-sanhita-2023" },
      { label: "Article 21 of the Constitution of India", url: "https://legislative.gov.in/constitution-of-india" },
      { label: "Supreme Court — Arnesh Kumar v. State of Bihar (2014)", url: "https://main.sci.gov.in" },
    ],
    lastUpdated: LAST_UPDATED,
    reviewedBy: REVIEWED,
  },

  "what-it-costs": {
    slug: "what-it-costs",
    order: 2,
    icon: "HeartHandshake",
    title: {
      en: "What Bail Actually Costs in India — and What You Should NEVER Pay",
      hi: "भारत में ज़मानत की असली लागत — और क्या-क्या कभी नहीं देना है",
    },
    metaTitle: {
      en: "Bail Lawyer Fees in India (2026) — Real Ranges + What NOT to Pay | Rihai",
      hi: "भारत में ज़मानत वकील की फ़ीस (2026) — असली रेंज + क्या नहीं देना है | Rihai",
    },
    metaDescription: {
      en: "Real bail lawyer fee ranges across Sessions Court and High Court in India (2026). Plus the exact list of bribes — munshi, peshkar, steno — that you should NEVER pay. Empathetic, honest, cited.",
      hi: "भारत में सत्र और उच्च न्यायालय में असली ज़मानत वकील की फ़ीस रेंज (2026)। साथ ही मुंशी, पेशकार, स्टेनो — जो कभी नहीं देनी हैं।",
    },
    intro: {
      en: "The single biggest reason families are exploited in India's legal system is that no one tells them what things actually cost. We will. Below are honest fee ranges (from public bar-association data and our own vetted network) for what a bail lawyer legitimately charges — and a second list of exactly what strangers around the court will demand, and what you should refuse.",
      hi: "भारतीय न्याय व्यवस्था में परिवारों के शोषण का सबसे बड़ा कारण यह है कि किसी ने असली लागत नहीं बताई। हम बताएँगे। नीचे ज़मानत वकील की ईमानदार रेंज है (सार्वजनिक बार-एसोसिएशन डेटा और हमारे सत्यापित नेटवर्क से) — और एक दूसरी सूची जो अदालत के आसपास अजनबी माँगेंगे, जिन्हें आपको मना करना है।",
    },
    keyPoints: {
      en: [
        "Bail lawyer fees vary by court level, seniority, and case complexity — but they DO have ranges.",
        "You should never pay anyone at the court who isn't your lawyer. Ever. Not one rupee.",
        "Government court fees are fixed and tiny — usually under ₹500.",
        "Any 'expediting fee' asked by court staff is a bribe. Refuse it. Report if needed.",
      ],
      hi: [
        "वकील की फ़ीस अदालत के स्तर, वरिष्ठता और मामले की जटिलता पर निर्भर करती है — लेकिन इनकी रेंज होती है।",
        "अदालत में आपके वकील के अलावा किसी को भी पैसा नहीं देना। कभी नहीं। एक रुपया भी नहीं।",
        "सरकारी अदालत शुल्क तय और छोटा है — आमतौर पर ₹500 से कम।",
        "अदालत के स्टाफ द्वारा माँगा गया कोई भी 'तेज़ी शुल्क' रिश्वत है। मना कीजिए। ज़रूरत पड़े तो रिपोर्ट कीजिए।",
      ],
    },
    costTable: [
      { item: { en: "Regular bail at Sessions Court", hi: "सत्र न्यायालय में नियमित ज़मानत" }, range: "₹15,000 – ₹40,000", note: { en: "Includes drafting, filing, and 1–2 hearings", hi: "मसौदा, दाख़िला, 1–2 सुनवाइयाँ शामिल" } },
      { item: { en: "Anticipatory bail at Sessions Court", hi: "सत्र न्यायालय में अग्रिम ज़मानत" }, range: "₹25,000 – ₹75,000", note: { en: "More discretionary, more preparation", hi: "अधिक विवेकाधीन, ज़्यादा तैयारी" } },
      { item: { en: "Regular bail at High Court", hi: "उच्च न्यायालय में नियमित ज़मानत" }, range: "₹40,000 – ₹1,50,000", note: { en: "Senior advocate required for serious matters", hi: "गंभीर मामलों में वरिष्ठ अधिवक्ता ज़रूरी" } },
      { item: { en: "Anticipatory bail at High Court", hi: "उच्च न्यायालय में अग्रिम ज़मानत" }, range: "₹60,000 – ₹2,00,000", note: { en: "Includes appearance in urgent listing", hi: "तात्कालिक सूचीबद्धता में उपस्थिति शामिल" } },
      { item: { en: "Government court fees", hi: "सरकारी अदालत शुल्क" }, range: "₹50 – ₹500", note: { en: "Stamp paper + filing fees only", hi: "स्टांप पेपर + फ़ाइलिंग शुल्क" } },
    ],
    dontPay: {
      title: { en: "What you should NEVER pay — the shakedown menu", hi: "जो कभी नहीं देना — वसूली सूची" },
      intro: {
        en: "The middlemen around every Indian courthouse have unofficial 'rate cards'. Here's what they typically ask for, and what each one actually is. Every entry below is a bribe. Refuse every single one. If your lawyer says these are 'normal,' find a new lawyer.",
        hi: "हर भारतीय अदालत के आसपास बिचौलियों के अनौपचारिक 'रेट कार्ड' होते हैं। यहाँ जो आमतौर पर माँगा जाता है और असल में वो क्या है। नीचे हर एक रिश्वत है। हर एक को मना कीजिए। अगर आपका वकील कहे 'यह सामान्य है' — नया वकील ढूँढिए।",
      },
      items: [
        { role: { en: "Munshi (lawyer's fixer)", hi: "मुंशी (वकील का बिचौलिया)" }, amount: "₹500 – ₹5,000", why: { en: "'To get your paperwork moving faster.' A myth.", hi: "'कागज़ जल्दी चलवाने के लिए।' एक मिथक।" } },
        { role: { en: "Peshkar (court clerk)", hi: "पेशकार (अदालत लिपिक)" }, amount: "₹200 – ₹500", why: { en: "'To ensure your file is listed today.' Listing is automatic.", hi: "'फ़ाइल आज सूचीबद्ध कराने के लिए।' सूचीबद्धता अपने आप होती है।" } },
        { role: { en: "Steno / typist", hi: "स्टेनो / टाइपिस्ट" }, amount: "₹100 – ₹300", why: { en: "'To type your order copy fast.' Order copy is a right.", hi: "'आदेश जल्दी टाइप कराने के लिए।' आदेश की प्रति एक अधिकार है।" } },
        { role: { en: "Dallaal outside court", hi: "अदालत के बाहर दलाल" }, amount: "₹5,000 – ₹50,000", why: { en: "'I know a lawyer who guarantees bail.' No one can guarantee bail. That itself is illegal.", hi: "'मेरे परिचय में वकील है, ज़मानत की गारंटी।' कोई ज़मानत की गारंटी नहीं दे सकता। यह ग़ैर-कानूनी है।" } },
        { role: { en: "Police 'facilitation'", hi: "पुलिस 'सुविधा शुल्क'" }, amount: "₹1,000 – ₹20,000", why: { en: "'To not oppose your bail.' Police cannot lawfully take money for this. Report to SHO or online IPC portal.", hi: "'ज़मानत का विरोध न करने के लिए।' पुलिस क़ानूनी रूप से यह पैसा नहीं ले सकती। SHO या ऑनलाइन शिकायत पोर्टल पर रिपोर्ट कीजिए।" } },
      ],
    },
    faqs: [
      {
        q: { en: "How much should a bail lawyer cost in India?", hi: "भारत में ज़मानत वकील की फ़ीस कितनी होनी चाहिए?" },
        a: {
          en: "For a regular bail application at Sessions Court, ₹15,000–₹40,000 is a fair range. At High Court, ₹40,000–₹1,50,000 depending on seniority of counsel and case complexity. Government court fees are separate and typically under ₹500.",
          hi: "सत्र न्यायालय में नियमित ज़मानत के लिए ₹15,000–₹40,000 उचित रेंज है। उच्च न्यायालय में ₹40,000–₹1,50,000, वकील की वरिष्ठता और मामले पर निर्भर। सरकारी शुल्क अलग, आमतौर पर ₹500 से कम।",
        },
      },
      {
        q: { en: "Is a lawyer allowed to guarantee bail?", hi: "क्या वकील ज़मानत की गारंटी दे सकता है?" },
        a: {
          en: "No. Under Rule 39 of the Bar Council of India Rules, an advocate cannot guarantee the outcome of any case. If someone tells you they guarantee bail for a fee — they are lying, and they are violating the law. Walk away.",
          hi: "नहीं। बार काउंसिल ऑफ इंडिया के नियम 39 के तहत, कोई भी वकील किसी भी मामले के परिणाम की गारंटी नहीं दे सकता। जो कहे कि पैसे पर ज़मानत की गारंटी है — वह झूठ है और क़ानून का उल्लंघन। दूर हो जाइए।",
        },
      },
      {
        q: { en: "How do I report court-staff bribery in India?", hi: "अदालत के स्टाफ की रिश्वत कैसे रिपोर्ट करूँ?" },
        a: {
          en: "Complaints can be filed with the Registrar of the concerned High Court, the CVC (Central Vigilance Commission) at cvc.gov.in, or the CBI anti-corruption cell. Under Section 7 of the Prevention of Corruption Act, both giver and taker can be prosecuted — but complainants under duress are protected.",
          hi: "शिकायत संबंधित उच्च न्यायालय के रजिस्ट्रार, CVC (cvc.gov.in) या CBI भ्रष्टाचार-रोधी शाखा में दर्ज हो सकती है। भ्रष्टाचार निवारण अधिनियम की धारा 7 के तहत देने वाला और लेने वाला दोनों दंडनीय हैं — मजबूरी में दिए शिकायतकर्ता को सुरक्षा है।",
        },
      },
    ],
    sources: [
      { label: "Bar Council of India — Standards of Professional Conduct", url: "https://www.barcouncilofindia.org" },
      { label: "Prevention of Corruption Act, 1988", url: "https://legislative.gov.in" },
      { label: "Central Vigilance Commission", url: "https://cvc.gov.in" },
    ],
    lastUpdated: LAST_UPDATED,
    reviewedBy: REVIEWED,
  },

  "timeline": {
    slug: "timeline",
    order: 3,
    icon: "Clock",
    title: {
      en: "How Long Does Bail Take in India? A Realistic Timeline",
      hi: "भारत में ज़मानत में कितना समय लगता है? एक असली समय-रेखा",
    },
    metaTitle: {
      en: "How Long Does Bail Take in India? Full 2026 Timeline | Rihai",
      hi: "भारत में ज़मानत में कितना समय? 2026 पूरी समय-रेखा | Rihai",
    },
    metaDescription: {
      en: "Realistic day-by-day bail timeline in India — Magistrate to Sessions to High Court. What actually delays it, and what you can do to move faster. Empathetic, cited.",
      hi: "भारत में असली ज़मानत समय-रेखा — मजिस्ट्रेट से सत्र से उच्च न्यायालय तक। असली देरी और तेज़ी के तरीक़े।",
    },
    intro: {
      en: "'How long?' is the question every family asks first — and the one most lawyers dodge. Here is the honest answer, court-by-court, drawn from what actually happens in Indian courts in 2026. It is not a promise. It is a realistic map.",
      hi: "'कितना समय?' — हर परिवार का पहला सवाल, और वकील का सबसे टालाया गया जवाब। यहाँ ईमानदार जवाब है, अदालत-दर-अदालत, जो 2026 में असल में होता है। यह वादा नहीं। यह असली नक़्शा है।",
    },
    keyPoints: {
      en: [
        "Bailable offence at police station: same day (as fast as sureties can be produced).",
        "Non-bailable, Magistrate: same day to 2 working days if properly moved.",
        "Sessions Court: 2–7 working days typically; urgent applications can be mentioned same-day.",
        "High Court: 3–15 working days depending on the roster and urgency of the matter.",
        "Anticipatory bail: usually 3–10 working days from filing to decision.",
      ],
      hi: [
        "पुलिस स्टेशन पर ज़मानती अपराध: उसी दिन (जितनी जल्दी ज़मानतदार पेश हों)।",
        "मजिस्ट्रेट में ग़ैर-ज़मानती: सही ढंग से दायर करने पर उसी दिन से 2 कार्य दिवस।",
        "सत्र न्यायालय: आमतौर पर 2–7 कार्य दिवस; तात्कालिक आवेदन उसी दिन उठाए जा सकते हैं।",
        "उच्च न्यायालय: रोस्टर और तात्कालिकता पर 3–15 कार्य दिवस।",
        "अग्रिम ज़मानत: आमतौर पर दाख़िले से निर्णय तक 3–10 कार्य दिवस।",
      ],
    },
    timeline: [
      { day: { en: "Day 0", hi: "दिन 0" }, event: { en: "Arrest. FIR copy requested. Police must produce accused before magistrate within 24 hours.", hi: "गिरफ़्तारी। FIR की प्रति माँगें। पुलिस को 24 घंटे में मजिस्ट्रेट के सामने पेश करना ज़रूरी।" } },
      { day: { en: "Day 1", hi: "दिन 1" }, event: { en: "First remand. Bail application for bailable offences can be moved right here. Non-bailable begins.", hi: "पहली रिमांड। ज़मानती अपराधों की ज़मानत यहीं दायर हो सकती है। ग़ैर-ज़मानती की शुरुआत।" } },
      { day: { en: "Day 2–7", hi: "दिन 2–7" }, event: { en: "Sessions Court bail application filed if magistrate refuses. Written arguments, prosecution reply.", hi: "मजिस्ट्रेट के इनकार पर सत्र न्यायालय में ज़मानत आवेदन। लिखित तर्क, अभियोजन जवाब।" } },
      { day: { en: "Day 5–15", hi: "दिन 5–15" }, event: { en: "High Court application if sessions refuses. Urgent mentioning may compress this.", hi: "सत्र के इनकार पर उच्च न्यायालय में आवेदन। तात्कालिक मेंशनिंग समय को छोटा कर सकती है।" } },
      { day: { en: "Day 15+", hi: "दिन 15+" }, event: { en: "Order pronounced. If granted, release from jail is usually within 24–48 hours of paperwork.", hi: "आदेश सुनाया गया। मंज़ूर होने पर, आमतौर पर कागज़ी कार्यवाही के 24–48 घंटे में रिहाई।" } },
    ],
    delays: {
      title: { en: "What actually causes delays", hi: "देरी असल में क्यों होती है" },
      items: {
        en: [
          "Filing errors in the bail application (avoidable with a competent lawyer)",
          "Police 'diary' not being ready — prosecution asks for adjournment",
          "Judge on leave / court on vacation / bench roster change",
          "Complainant/informant seeking to intervene",
          "Cases involving multiple accused — court prefers a single order",
        ],
        hi: [
          "ज़मानत आवेदन में गलतियाँ (सक्षम वकील से बचा जा सकता है)",
          "पुलिस डायरी तैयार न होना — अभियोजन स्थगन माँगता है",
          "जज छुट्टी पर / अदालत अवकाश / बेंच रोस्टर बदलाव",
          "शिकायतकर्ता का हस्तक्षेप",
          "कई आरोपियों वाले मामले — अदालत एक साथ आदेश पसंद करती है",
        ],
      },
    },
    faqs: [
      {
        q: { en: "Can bail be granted the same day of arrest?", hi: "क्या गिरफ़्तारी के दिन ही ज़मानत मिल सकती है?" },
        a: {
          en: "Yes — for bailable offences, at the police station itself once sureties are produced. For non-bailable, the magistrate can grant bail the same day if the application is moved and the offence permits it under Section 480 BNSS.",
          hi: "हाँ — ज़मानती अपराधों में, पुलिस स्टेशन पर ही ज़मानतदार पेश करते ही। ग़ैर-ज़मानती में, धारा 480 BNSS के अनुसार, आवेदन दायर करने पर मजिस्ट्रेट उसी दिन ज़मानत दे सकता है।",
        },
      },
      {
        q: { en: "What if the court is closed on the day of arrest?", hi: "अगर गिरफ़्तारी के दिन अदालत बंद हो?" },
        a: {
          en: "The nearest duty magistrate must be produced within 24 hours — this is a constitutional guarantee under Article 22(2). Bail applications can be moved before duty magistrates during vacations and weekends.",
          hi: "24 घंटे में निकटतम ड्यूटी मजिस्ट्रेट के सामने पेश करना ज़रूरी — यह अनुच्छेद 22(2) की संवैधानिक गारंटी है। अवकाश और सप्ताहांत में भी ड्यूटी मजिस्ट्रेट के सामने आवेदन हो सकते हैं।",
        },
      },
    ],
    sources: [
      { label: "Article 22 of the Constitution of India", url: "https://legislative.gov.in/constitution-of-india" },
      { label: "BNSS Section 187 — Judicial custody & remand", url: "https://www.mha.gov.in" },
      { label: "Supreme Court — Satender Kumar Antil v. CBI (2022)", url: "https://main.sci.gov.in" },
    ],
    lastUpdated: LAST_UPDATED,
    reviewedBy: REVIEWED,
  },

  "legal-terms": {
    slug: "legal-terms",
    order: 4,
    icon: "BookOpen",
    title: {
      en: "Legal Terms Every Indian Family Should Know — A Glossary",
      hi: "हर भारतीय परिवार को पता होने चाहिए ये कानूनी शब्द — एक शब्दकोश",
    },
    metaTitle: {
      en: "Indian Legal Terms Glossary — Bail, FIR, Remand, BNSS Explained | Rihai",
      hi: "भारतीय कानूनी शब्दकोश — ज़मानत, FIR, रिमांड, BNSS समझाया | Rihai",
    },
    metaDescription: {
      en: "The essential legal terms every family needs when a loved one is arrested — FIR, remand, bail types, chargesheet, BNSS, cognizable offence. Plain-language, bilingual.",
      hi: "जब अपनों की गिरफ़्तारी हो, तब हर परिवार को ज़रूरी कानूनी शब्द — FIR, रिमांड, ज़मानत के प्रकार, चार्जशीट, BNSS, संज्ञेय अपराध। सरल भाषा में।",
    },
    intro: {
      en: "The Indian legal system speaks in a private language. That's a large part of why families feel lost. Here is that language, translated. Learn these terms and half the fear disappears.",
      hi: "भारतीय न्याय व्यवस्था एक अपनी भाषा बोलती है। इसीलिए परिवार खो जाते हैं। यहाँ वही भाषा, अनुवादित। ये शब्द सीखिए और आधा डर ग़ायब हो जाता है।",
    },
    keyPoints: {
      en: [
        "The CrPC no longer exists — since 1 July 2024 it is the BNSS 2023.",
        "IPC has been replaced by the BNS 2023 (Bharatiya Nyaya Sanhita).",
        "Under Article 21, no one can be deprived of liberty except by procedure established by law.",
        "You are entitled to a free copy of the FIR — no one can charge you for it.",
        "If the chargesheet is not filed in 60/90 days, default bail is your right.",
      ],
      hi: [
        "CrPC अब मौजूद नहीं है — 1 जुलाई 2024 से BNSS 2023 लागू है।",
        "IPC को BNS 2023 (भारतीय न्याय संहिता) ने बदल दिया है।",
        "अनुच्छेद 21 के तहत, क़ानूनी प्रक्रिया के बिना किसी को स्वतंत्रता से वंचित नहीं किया जा सकता।",
        "आपको FIR की मुफ़्त प्रति पाने का अधिकार है — कोई शुल्क नहीं ले सकता।",
        "60/90 दिनों में चार्जशीट न आए तो डिफ़ॉल्ट ज़मानत आपका अधिकार है।",
      ],
    },
    glossary: [
      { term: { en: "FIR (First Information Report)", hi: "FIR (प्रथम सूचना रिपोर्ट)" }, def: { en: "The first document created when a cognizable offence is reported to police. Under Section 173 BNSS, police MUST register it if the information discloses a cognizable offence. You are entitled to a free copy.", hi: "पुलिस को संज्ञेय अपराध की सूचना पर बनने वाला पहला दस्तावेज़। BNSS धारा 173 के तहत, संज्ञेय अपराध होने पर पुलिस को दर्ज करना अनिवार्य। आपको मुफ़्त प्रति पाने का अधिकार है।" } },
      { term: { en: "Cognizable vs Non-cognizable Offence", hi: "संज्ञेय बनाम ग़ैर-संज्ञेय अपराध" }, def: { en: "Cognizable = police can arrest without a warrant (serious crimes). Non-cognizable = police need magistrate's permission (minor offences). Determines everything that follows.", hi: "संज्ञेय = पुलिस बिना वारंट गिरफ़्तार कर सकती है (गंभीर अपराध)। ग़ैर-संज्ञेय = मजिस्ट्रेट की अनुमति चाहिए (छोटे अपराध)। बाकी सब कुछ इसी पर निर्भर।" } },
      { term: { en: "Remand", hi: "रिमांड" }, def: { en: "Court's order sending the accused to police or judicial custody while investigation continues. Police custody max 15 days. Judicial custody up to 60 or 90 days depending on offence.", hi: "जाँच जारी रहते हुए आरोपी को पुलिस या न्यायिक हिरासत में भेजने का अदालती आदेश। पुलिस हिरासत अधिकतम 15 दिन। न्यायिक हिरासत अपराध के अनुसार 60 या 90 दिन तक।" } },
      { term: { en: "Chargesheet", hi: "चार्जशीट" }, def: { en: "Final police report filed under Section 193 BNSS after investigation, naming the accused and charges. If not filed within 60/90 days, the accused gets default bail under Section 187(3) BNSS.", hi: "जाँच के बाद BNSS धारा 193 के तहत अंतिम पुलिस रिपोर्ट, जिसमें आरोपी और आरोप लिखे होते हैं। 60/90 दिनों में दाख़िल न होने पर, आरोपी को धारा 187(3) BNSS के तहत डिफ़ॉल्ट ज़मानत मिलती है।" } },
      { term: { en: "Surety", hi: "ज़मानतदार" }, def: { en: "A person who signs a bond guaranteeing the accused will appear in court. Money is only forfeited if the accused absconds. Usually a family member with verifiable ID.", hi: "एक व्यक्ति जो बांड पर हस्ताक्षर करके गारंटी देता है कि आरोपी अदालत में आएगा। पैसा तभी ज़ब्त होता है जब आरोपी फ़रार हो। आमतौर पर सत्यापित पहचान वाला परिवार का सदस्य।" } },
      { term: { en: "Bailable vs Non-bailable", hi: "ज़मानती बनाम ग़ैर-ज़मानती" }, def: { en: "Not related to whether bail is possible — every offence is bailable in some form. This refers to whether bail is a matter of right (bailable) or court discretion (non-bailable).", hi: "यह इस पर नहीं है कि ज़मानत मिल सकती है या नहीं — हर अपराध में किसी न किसी रूप में ज़मानत संभव है। यह इस पर है कि ज़मानत अधिकार से मिलेगी (ज़मानती) या अदालत के विवेक पर (ग़ैर-ज़मानती)।" } },
      { term: { en: "BNSS 2023", hi: "BNSS 2023" }, def: { en: "Bharatiya Nagarik Suraksha Sanhita — the new criminal procedure code that replaced the CrPC on 1 July 2024. Governs arrest, investigation, bail, trial procedure.", hi: "भारतीय नागरिक सुरक्षा संहिता — नया आपराधिक प्रक्रिया कोड जो 1 जुलाई 2024 को CrPC को बदला। गिरफ़्तारी, जाँच, ज़मानत, मुक़दमा प्रक्रिया इसी से चलती है।" } },
      { term: { en: "BNS 2023", hi: "BNS 2023" }, def: { en: "Bharatiya Nyaya Sanhita — the new penal code that replaced the IPC. Defines what is a crime and its punishment. Old IPC sections have new BNS section numbers.", hi: "भारतीय न्याय संहिता — नई दंड संहिता जिसने IPC को बदला। बताती है कि अपराध क्या है और सज़ा क्या। पुरानी IPC धाराओं की नई BNS धाराएँ हैं।" } },
      { term: { en: "Article 21", hi: "अनुच्छेद 21" }, def: { en: "Constitutional right to life and personal liberty. The bedrock of bail jurisprudence in India — no person can be deprived of liberty except by procedure established by law.", hi: "जीवन और व्यक्तिगत स्वतंत्रता का संवैधानिक अधिकार। भारत में ज़मानत क़ानून की नींव — क़ानूनी प्रक्रिया के बिना किसी को स्वतंत्रता से वंचित नहीं किया जा सकता।" } },
      { term: { en: "Default Bail", hi: "डिफ़ॉल्ट ज़मानत" }, def: { en: "Automatic bail granted if the chargesheet is not filed within the statutory period (60 or 90 days). A powerful protection under Section 187(3) BNSS.", hi: "यदि क़ानूनी अवधि (60 या 90 दिन) में चार्जशीट दाख़िल न हो, तो अपने आप मिलने वाली ज़मानत। BNSS धारा 187(3) के तहत एक शक्तिशाली सुरक्षा।" } },
    ],
    faqs: [
      {
        q: { en: "Is the CrPC still in force in 2026?", hi: "क्या 2026 में CrPC अब भी लागू है?" },
        a: {
          en: "No. The CrPC was repealed and replaced by the Bharatiya Nagarik Suraksha Sanhita (BNSS) 2023 with effect from 1 July 2024. Cases registered before that date may still proceed under CrPC — but new FIRs are under BNSS.",
          hi: "नहीं। CrPC को 1 जुलाई 2024 से भारतीय नागरिक सुरक्षा संहिता (BNSS) 2023 ने बदल दिया। उस तारीख से पहले दर्ज मामले CrPC में जारी रह सकते हैं — लेकिन नई FIR BNSS के तहत हैं।",
        },
      },
      {
        q: { en: "What's the difference between BNS and BNSS?", hi: "BNS और BNSS में क्या अंतर है?" },
        a: {
          en: "BNS (Bharatiya Nyaya Sanhita) defines crimes and their punishments — replaces IPC. BNSS (Bharatiya Nagarik Suraksha Sanhita) defines procedure — replaces CrPC. BSA (Bharatiya Sakshya Adhiniyam) replaces the Evidence Act. Three new laws, one big shift.",
          hi: "BNS (भारतीय न्याय संहिता) अपराध और सज़ा तय करती है — IPC को बदला। BNSS (भारतीय नागरिक सुरक्षा संहिता) प्रक्रिया तय करती है — CrPC को बदला। BSA (भारतीय साक्ष्य अधिनियम) एविडेंस एक्ट को बदला। तीन नए क़ानून।",
        },
      },
    ],
    sources: [
      { label: "Bharatiya Nyaya Sanhita, 2023 (BNS)", url: "https://www.mha.gov.in" },
      { label: "Bharatiya Nagarik Suraksha Sanhita, 2023 (BNSS)", url: "https://www.mha.gov.in" },
      { label: "Constitution of India — Articles 20, 21, 22", url: "https://legislative.gov.in" },
    ],
    lastUpdated: LAST_UPDATED,
    reviewedBy: REVIEWED,
  },
};

export const GUIDE_ORDER = ["bail-process", "what-it-costs", "timeline", "legal-terms"];
export const getGuideBySlug = (slug) => GUIDES[slug] || null;

// Slim card renderer for guide blocks — enough structure for the
// unified /blog hub to show every guide section with its links.
export const renderGuideSections = (g, lang, Link) => {
  const parts = [];
  g.intro?.forEach((p, i) =>
    parts.push(
      <p key={`intro-${i}`} className="mb-4 text-[#4A4640] leading-relaxed">
        {pick(p, lang)}
      </p>
    )
  );
  if (g.steps) {
    parts.push(
      <ol key="steps" className="list-decimal ml-5 space-y-2 mb-4">
        {g.steps.map((s, i) => (
          <li key={i} className="text-[#4A4640] leading-relaxed">
            {pick(s, lang)}
          </li>
        ))}
      </ol>
    );
  }
  if (g.timeline) {
    parts.push(
      <div key="tl" className="space-y-3 mb-4">
        {g.timeline.map((t, i) => (
          <div key={i} className="border-l-2 border-[#C46B2F] pl-4">
            <div className="text-xs font-medium uppercase tracking-widest text-[#C46B2F]">
              {pick(t.day, lang)}
            </div>
            <div className="text-sm text-[#4A4640]">{pick(t.event, lang)}</div>
          </div>
        ))}
      </div>
    );
  }
  if (g.faq) {
    parts.push(
      <div key="faq" className="space-y-2">
        {g.faq.map((f, i) => (
          <details key={i} className="border-b border-[#EAE6DF] pb-2">
            <summary className="text-sm font-medium text-[#1A1A1A] cursor-pointer">
              {pick(f.q, lang)}
            </summary>
            <p className="text-sm text-[#4A4640] mt-1">{pick(f.a, lang)}</p>
          </details>
        ))}
      </div>
    );
  }
  return parts;
};
